import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import { reviewToolsEnabled } from './reviewToolsCapability.js';

function ProblemIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.3 3.6 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function RequestIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v18" />
      <path d="M3 12h18" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2a2.8 2.8 0 1 1 3.9 2.6c-.7.3-1.1 1-1.1 1.7v.3" />
      <path d="M12 17.5h.01" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="6" width="20" height="14" rx="2" />
      <circle cx="12" cy="13" r="4" />
      <path d="M8 6l1.5-2.5h5L16 6" />
    </svg>
  );
}

function CropIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 2v14a2 2 0 0 0 2 2h14" />
      <path d="M18 22V8a2 2 0 0 0-2-2H2" />
    </svg>
  );
}

function SelectElementIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 3v14l4-4 3 7 3-1-3-7h6L5 3z" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="3.5" cy="6" r="1" />
      <circle cx="3.5" cy="12" r="1" />
      <circle cx="3.5" cy="18" r="1" />
    </svg>
  );
}

export function FeedbackMenu() {
  const {
    state,
    captureScreen,
    beginRegionSelection,
    beginElementSelection,
    closeFeedback,
    resumeFeedback,
    navigate,
  } = useFeedbackMarkup();
  const capturing = state.captureStatus === 'capturing';
  // Lifecycle totals, not the last-loaded filtered page: a tester with 12
  // submitted packages who last viewed an empty Drafts tab must not read
  // "0 packages".
  const lifecycleCounts = state.historyMeta?.lifecycleCounts || null;
  const historyCount = lifecycleCounts
    ? Number(lifecycleCounts.draft || 0) +
      Number(lifecycleCounts.submitted || 0) +
      Number(lifecycleCounts.resolved || 0)
    : Array.isArray(state.historyItems)
      ? state.historyItems.length
      : null;
  const historySummary =
    historyCount == null
      ? 'Track status and replies'
      : `${historyCount} package${historyCount === 1 ? '' : 's'} · track status and replies`;
  const packageScreens = state.package?.screens || [];
  const hasInactiveDraft = Boolean(state.resumableDraft && !state.package);
  const canResumeEditing =
    hasInactiveDraft ||
    (state.package?.status !== 'submitted' && Boolean(state.stage || packageScreens.length));

  // Typed help/intake framework, commit 2. The SCREEN-CAPTURE group keeps the
  // VITE_ENABLE_REVIEW_TOOLS gate it has always had (it is a demo/review tool);
  // "Report a problem" and "My Feedback" do not, because a flag named "review
  // tools" must not decide whether a real user can reach the service desk.
  const captureToolsEnabled = reviewToolsEnabled();

  function openFeedbackPage() {
    closeFeedback();
    navigate('/feedback');
  }

  function resumeEditing() {
    if (hasInactiveDraft) {
      resumeFeedback();
      return;
    }
    closeFeedback();
    navigate(state.stage ? '/feedback/workspace' : '/feedback?view=drafts');
  }

  // The route the reporter was ON becomes the ticket's technical context. Read
  // from window rather than useLocation so this menu stays renderable outside a
  // Router (it is mounted from the topbar popover, not from a route element).
  function reportProblem() {
    closeFeedback();
    const fromRoute = typeof window === 'undefined' ? '/' : window.location?.pathname || '/';
    navigate('/feedback/support/new', { state: { fromRoute } });
  }

  // Typed help/intake framework, commit 4. Same primary group as "Report a
  // problem": asking for a capability is a first-class reason a real user opens
  // this menu, and it is not gated on VITE_ENABLE_REVIEW_TOOLS for the same reason
  // — a flag named "review tools" must not decide whether a user can reach the
  // product team.
  function requestFeature() {
    closeFeedback();
    const fromRoute = typeof window === 'undefined' ? '/' : window.location?.pathname || '/';
    navigate('/feedback/request/new', { state: { fromRoute } });
  }

  // Typed help/intake framework, commit 5. Completes the primary group: report a
  // problem, request a capability, ask a question. Not gated on
  // VITE_ENABLE_REVIEW_TOOLS for the same reason as its two siblings — a flag named
  // "review tools" must not decide whether a real user can ask PRIME a question.
  function askQuestion() {
    closeFeedback();
    const fromRoute = typeof window === 'undefined' ? '/' : window.location?.pathname || '/';
    navigate('/help/questions', { state: { fromRoute } });
  }

  return (
    <section
      className="feedback-menu fb-menu"
      data-testid="feedback-menu"
      role="menu"
      aria-label="Feedback"
    >
      <button
        type="button"
        className="fb-mi primary"
        data-testid="feedback-report-problem"
        role="menuitem"
        onClick={reportProblem}
      >
        <span className="mi-ic">
          <ProblemIcon />
        </span>
        <span>
          <b>Report a problem</b>
          <small>Send a ticket to the MHS Service Desk and track it here.</small>
        </span>
      </button>
      <button
        type="button"
        className="fb-mi primary"
        data-testid="feedback-request-feature"
        role="menuitem"
        onClick={requestFeature}
      >
        <span className="mi-ic">
          <RequestIcon />
        </span>
        <span>
          <b>Request a feature or service</b>
          <small>Answer a few questions and PRIME drafts it for the product team.</small>
        </span>
      </button>
      <button
        type="button"
        className="fb-mi primary"
        data-testid="feedback-ask-question"
        role="menuitem"
        onClick={askQuestion}
      >
        <span className="mi-ic">
          <QuestionIcon />
        </span>
        <span>
          <b>Ask a question</b>
          <small>PRIME answers from its knowledge base, or sends it to a person.</small>
        </span>
      </button>
      {captureToolsEnabled ? <div className="fb-sep" aria-hidden="true" /> : null}
      {captureToolsEnabled ? (
        <p className="fb-group-label" data-testid="feedback-capture-group-label">
          Give screen feedback
        </p>
      ) : null}
      {captureToolsEnabled && canResumeEditing ? (
        <button
          type="button"
          className="fb-mi primary"
          data-testid="feedback-resume-editing"
          role="menuitem"
          onClick={resumeEditing}
        >
          <span className="mi-ic">
            <ListIcon />
          </span>
          <span>
            <b>{hasInactiveDraft ? 'Resume feedback' : 'Resume editing'}</b>
            <small>
              {hasInactiveDraft
                ? 'Restore your saved feedback workspace.'
                : state.stage
                  ? 'Return to your open feedback workspace.'
                  : 'Open this draft in My Feedback.'}
            </small>
          </span>
        </button>
      ) : null}
      {captureToolsEnabled ? (
        <>
          <button
            type="button"
            className={`fb-mi ${canResumeEditing ? '' : 'primary'}`}
            data-testid="feedback-capture-screen"
            role="menuitem"
            onClick={captureScreen}
            disabled={capturing}
          >
            <span className="mi-ic">
              <CameraIcon />
            </span>
            <span>
              <b>Capture screen</b>
              <small>One click — grabs exactly what you see now.</small>
            </span>
          </button>
          <button
            type="button"
            className="fb-mi"
            data-testid="feedback-capture-region"
            role="menuitem"
            onClick={beginRegionSelection}
          >
            <span className="mi-ic">
              <CropIcon />
            </span>
            <span>
              <b>Capture region</b>
              <small>Drag over just the area you want to comment on.</small>
            </span>
          </button>
          <button
            type="button"
            className="fb-mi"
            data-testid="feedback-select-element"
            role="menuitem"
            onClick={beginElementSelection}
          >
            <span className="mi-ic">
              <SelectElementIcon />
            </span>
            <span>
              <b>Select element</b>
              <small>Point at a button, field, or card — no markup needed.</small>
            </span>
          </button>
        </>
      ) : null}
      <div className="fb-sep" aria-hidden="true" />
      <button
        type="button"
        className="fb-mi"
        data-testid="feedback-open-management"
        role="menuitem"
        onClick={openFeedbackPage}
      >
        <span className="mi-ic">
          <ListIcon />
        </span>
        <span>
          <b>My Feedback</b>
          <small>{historySummary}</small>
        </span>
      </button>
      {state.captureMessage || state.storageWarning ? (
        <div className="feedback-menu-status" aria-live="polite">
          {state.captureMessage ? <span>{state.captureMessage}</span> : null}
          {state.storageWarning ? <span>{state.storageWarning}</span> : null}
        </div>
      ) : null}
    </section>
  );
}
